/** Читалка канонического формата.
 *
 * Вся механика — загрузка, позиция, настройки — лежит в `useReader`: она общая
 * с языковой читалкой. Здесь остаётся только оформление страницы.
 */

import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { BlockView } from '../reader/blocks'
import { ChapterNav, ElsewhereToast, SettingsPanel, TocPanel } from '../reader/chrome'
import { useChapterKeys, useReader } from '../reader/useReader'
import { Spinner } from '../components/Spinner'

export function ReaderPage() {
  const { id } = useParams()
  const bookId = Number(id)
  const navigate = useNavigate()

  const reader = useReader(bookId)
  const [panel, setPanel] = useState<'toc' | 'settings' | null>(null)
  useChapterKeys(reader, () => setPanel(null))

  const { manifest, chapter, chapterIndex, progress, isLast, error, settings } = reader

  const goToChapter = (index: number, block = 0) => {
    reader.goToChapter(index, block)
    setPanel(null)
  }

  if (error) {
    return (
      <div className="center">
        <div>
          <p className="error">{error}</p>
          <Link className="btn" to={`/book/${bookId}`}>
            К карточке книги
          </Link>
        </div>
      </div>
    )
  }

  if (!manifest) return <Spinner />

  const info = manifest.chapters[chapterIndex]

  return (
    <div className="reader">
      <div className={`reader__bar${reader.barHidden ? ' reader__bar--hidden' : ''}`}>
        <button className="btn btn--ghost" onClick={() => navigate(`/book/${bookId}`)}>
          ← Назад
        </button>
        <div className="reader__title">
          {manifest.title}
          {info?.title ? ` · ${info.title}` : ''}
        </div>
        <button className="btn btn--ghost" onClick={() => setPanel('toc')}>
          Оглавление
        </button>
        <button className="btn btn--ghost" onClick={() => setPanel('settings')}>
          Вид
        </button>
      </div>

      <div className="reader__page">
        {!chapter ? (
          <Spinner />
        ) : (
          <>
            {chapter.blocks.map((block) => (
              <div
                key={block.i}
                data-block={block.i}
                ref={(element) => reader.registerBlock(block.i, element)}
              >
                <BlockView block={block} bookId={bookId} />
              </div>
            ))}
            <ChapterNav index={chapterIndex} isLast={isLast} onGo={goToChapter} />
          </>
        )}
      </div>

      <div className="reader__progress">
        <span style={{ width: `${(progress * 100).toFixed(2)}%` }} />
      </div>

      {panel && <div className="overlay" onClick={() => setPanel(null)} />}

      {panel === 'toc' && (
        <TocPanel manifest={manifest} current={chapterIndex} onPick={goToChapter} />
      )}

      {panel === 'settings' && (
        <SettingsPanel
          settings={settings}
          onChange={reader.updateSettings}
          onClose={() => setPanel(null)}
        />
      )}

      <ElsewhereToast reader={reader} />
    </div>
  )
}
